import GridTable from '@/components/gridTable/GridTable';
import Info from '@/components/Info';
import Input from '@/components/Input';
import ModalV2 from '@/components/ModalV2';
import Page from '@/components/page/Page';
import { FiSearch } from '@/components/react-icons/main';
import IconV2 from '@/components/v2/IconV2';
import ScrollContainerV2 from '@/components/v2/ScrollContainerV2';
import { getAllIssues, getCompanyWorkflows, getIntegrationsProjects, getIntegrators, getServiceUsers } from '@/redux/actions/gravityActions';
import { setModalState } from '@/redux/actions/uiActions/modalActions';
import { getUserCompanies } from '@/redux/actions/userActions';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors';
import selectors from '@/redux/selectors/selectors';
import { ISSUE_STATUS_OPTIONS, PRIORITY_OPTIONS, STATUS_OPTIONS, PROJECT_HEALTH_OPTIONS, PROJECT_HEALTH_COLORS, getIssueStatusStyle, getPriorityStyle, getStatusStyle } from '@/screens/monitoring/lib/gridConstants';
import ProjectHealthModal from '@/screens/monitoring/components/ProjectHealthModal';
import projectActions from '@/screens/workflows/redux/actions/projectActions.js';
import projectIssueActions from '@/screens/workflows/redux/actions/projectIssueActions.js';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FiChevronRight } from 'react-icons/fi';
import { SiLinear } from 'react-icons/si';
import { useSelector } from 'react-redux';

const PRIORITY_SORT_ORDER = { 1: 0, 2: 1, 3: 2, 4: 3, 0: 4 };

const ISSUE_STATE_SORT_ORDER = {
  Backlog: 0,
  Todo: 1,
  'In Progress': 2,
  'In Review': 3,
  'UAT / Client Review': 4,
  'Ready for Go-Live': 5,
  Live: 6,
  Done: 7,
  Blocked: 8,
  Canceled: 9
};

const IntegrationsOverviewModal = () => {
  // We keep using the existing modal keys in Redux:
  // - projectsList → projects view (with per-project issues)
  // - issuesList → cross-project issues view
  const projectsModal = useSelector((state) => state?.ui?.modals?.projectsList);
  const issuesModal = useSelector((state) => state?.ui?.modals?.issuesList);

  const projectsOpen = projectsModal?.open || false;
  const issuesOpen = issuesModal?.open || false;

  const mode = issuesOpen ? 'issues' : 'projects';
  const show = projectsOpen || issuesOpen;

  const activeModalData = (mode === 'issues' ? issuesModal : projectsModal)?.data || {};
  const { type, status, issueStatus, issueStatuses, integrator, pmId, startDate, endDate, metric } = activeModalData;

  const loadingProjects = useArgSelector(selectors.isLoading, 'getIntegrationsProjects');
  const loadingProjectIssues = useArgSelector(selectors.isLoading, 'getAllIssues');
  const loadingAllIssues = useArgSelector(selectors.isLoading, 'getAllIssues');

  const projects = useSelector((state) => state.settings.integrationsProjects) || [];
  const issuesStore = useSelector(selectors.projectIssues) || [];

  const userId = useSelector(selectors.userId);
  const userCompanies = useSelector(selectors.userCompanies) || [];

  const [currentView, setCurrentView] = useState('projects'); // 'projects' | 'issues'
  const [selectedProject, setSelectedProject] = useState(null);

  const [projectSearchQuery, setProjectSearchQuery] = useState('');
  const [issuesSearchQuery, setIssuesSearchQuery] = useState('');

  const [issueProjects, setIssueProjects] = useState([]);
  const [issueProjectsOptions, setIssueProjectsOptions] = useState([]);
  const [integratorsOptions, setIntegratorsOptions] = useState([]);
  const [accountRepOptions, setAccountRepOptions] = useState([]);

  const [currentWorkflowCompanyId, setCurrentWorkflowCompanyId] = useState('');
  const [healthModalProject, setHealthModalProject] = useState(null);

  const companyWorkflows = useArgSelector(selectors.workflowsByCompany, currentWorkflowCompanyId);

  const projectsCancelRef = useRef();
  const issuesCancelRef = useRef();
  const modalRef = useRef();

  const projectIssues = issuesStore;
  const allIssues = issuesStore;

  // Company options reused from grid views
  const companyOptions = useMemo(() => {
    const nonPersonalCompanies = userCompanies.filter((company) => company?.isDeleted !== true && company?.isPersonal !== true);

    return nonPersonalCompanies
      .map((company) => ({
        value: company.id,
        label: company.name
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [userCompanies]);

  const workflowOptions = useMemo(() => {
    const baseOptions = (companyWorkflows || []).map((wf) => ({
      label: wf.name,
      value: wf.id
    }));

    return [{ label: 'Not a Workflow', value: 'Not a Workflow' }, ...baseOptions];
  }, [companyWorkflows]);

  // Fetch data when modal opens
  useEffect(() => {
    if (!show) {
      return;
    }

    if (mode === 'projects') {
      setCurrentView('projects');
      setSelectedProject(null);
      setProjectSearchQuery('');
      setIssuesSearchQuery('');

      getIntegrationsProjects({ type, status, integrator, pmId, cancelRef: projectsCancelRef });

      getIntegrators({}, (result) => {
        const integratorOptions =
          result?.map((integratorItem) => ({
            label: integratorItem.name || integratorItem.user_id || integratorItem.userId,
            value: integratorItem.user_id || integratorItem.userId
          })) || [];

        setIntegratorsOptions(integratorOptions);
      });

      getServiceUsers({ roles: ['accounting-representative'] }, (result) => {
        const options =
          result?.map((rep) => ({
            label: rep.linearDisplayName,
            value: rep.userId
          })) || [];

        setAccountRepOptions(options);
      });
    } else {
      // mode === 'issues'
      setCurrentView('issues');
      setSelectedProject(null);
      setIssuesSearchQuery('');
      // Always fetch a fresh issues dataset that matches the current context
      const metricKey = activeModalData?.metric;
      const isTimelineMetric = metricKey === 'issuesIntoUat' || metricKey === 'issuesCompleted';

      getAllIssues({
        cancelRef: issuesCancelRef,
        integrator,
        pmId,
        type,
        issueStatus,
        issueStatuses,
        metric: isTimelineMetric ? metricKey : null,
        startDate: isTimelineMetric ? activeModalData?.startDate : null,
        endDate: isTimelineMetric ? activeModalData?.endDate : null
      });

      // Fetch all projects for dropdown / filtering
      getIntegrationsProjects({}, (result) => {
        const all = result || [];
        setIssueProjects(all);
        const projectOptions = all.map((project) => ({
          label: project.name,
          value: project.id
        }));
        setIssueProjectsOptions(projectOptions);
      });

      // Fetch integrators for assignee dropdown
      getIntegrators({}, (result) => {
        const integratorOptions =
          result?.map((integratorItem) => ({
            label: integratorItem.name || integratorItem.user_id || integratorItem.userId,
            value: integratorItem.user_id || integratorItem.userId
          })) || [];
        setIntegratorsOptions(integratorOptions);
      });
    }

    return () => {
      if (projectsCancelRef.current && typeof projectsCancelRef.current.cancel === 'function') {
        projectsCancelRef.current.cancel('Modal closed');
      }
      if (issuesCancelRef.current?.abort) {
        issuesCancelRef.current.abort();
      }
      if (issuesCancelRef.current && typeof issuesCancelRef.current.cancel === 'function') {
        issuesCancelRef.current.cancel('Modal closed');
      }
    };
  }, [show, mode, type, status, issueStatus, issueStatuses, integrator, pmId, metric, startDate, endDate]);

  // When viewing issues for a specific project (projects mode)
  useEffect(() => {
    if (mode !== 'projects') {
      return;
    }

    if (currentView === 'issues' && selectedProject) {
      setIssuesSearchQuery('');
      getAllIssues({ projectId: selectedProject.id, cancelRef: projectsCancelRef });
    }
  }, [mode, currentView, selectedProject]);

  const handleCompanySearch = (search) => {
    if (!search || !search.length) {
      return;
    }
    getUserCompanies({ userId, search });
  };

  const handleClose = () => {
    if (mode === 'projects') {
      setModalState('projectsList', { open: false });
    } else {
      setModalState('issuesList', { open: false });
    }
    setCurrentView('projects');
    setSelectedProject(null);
  };

  const handleProjectClick = (project) => {
    if (!project) {
      return;
    }
    setSelectedProject(project);
    setCurrentView('issues');
  };

  const handleBackToProjects = () => {
    setCurrentView('projects');
    setSelectedProject(null);
  };

  const handleIssueClick = (url) => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleProjectSearchChange = (e) => {
    setProjectSearchQuery(e.target.value);
  };

  const handleIssuesSearchChange = (e) => {
    setIssuesSearchQuery(e.target.value);
  };

  const shouldShowSearch = () => {
    if (!show) {
      return false;
    }

    if (mode === 'projects') {
      if (currentView === 'projects') {
        return projects.length > 8 || (projectSearchQuery && projectSearchQuery.trim().length > 0);
      }
      return projectIssues.length > 8 || (issuesSearchQuery && issuesSearchQuery.trim().length > 0);
    }

    // mode === 'issues'
    return allIssues.length > 8 || (issuesSearchQuery && issuesSearchQuery.trim().length > 0);
  };

  const getProjectsModalTitle = () => {
    if (type === 'backlogged') {
      return (
        <span className="title-with-info">
          Backlogged Projects
          <Info title={`Projects in the following statuses:\nNew Project, Client Setup in Progress, Requirements, Requirements Verification, Ready to Start, Building, UAT, Go Live\n\nA project is backlogged if:\n• In New Project: creation date is > 4 weeks ago\n• In Client Setup in Progress, Requirements, or Requirements Verification: it is not counted as backlogged\n• In Ready to Start, Building, UAT, or Go Live: current date is past the project due date, or 8 weeks past start date when there is no project due date`} />
        </span>
      );
    }
    if (type === 'falloff') {
      return 'Projects Falling Off Next Month';
    }
    if (type === 'oldestBacklog') {
      return 'Oldest Backlogged Project';
    }
    if (type === 'completed') {
      return 'Completed Projects (This Month)';
    }
    if (status) {
      return `${status}`;
    }
    return 'All Projects';
  };

  const getIssuesModalTitle = () => {
    const getIntegratorTitleSuffix = () => {
      if (!integrator) {
        return '';
      }

      if (integrator === 'unassigned') {
        return ' (Other)';
      }

      const match = (integratorsOptions || []).find((opt) => String(opt.value) === String(integrator));
      if (!match?.label) {
        return '';
      }

      return ` (${match.label})`;
    };

    const titleSuffix = getIntegratorTitleSuffix();

    const isSameSet = (a = [], b = []) => {
      if (!Array.isArray(a) || !Array.isArray(b)) {
        return false;
      }

      if (a.length !== b.length) {
        return false;
      }

      const setA = new Set(a);
      return b.every((item) => setA.has(item));
    };

    const BUILDING_ISSUE_STATUSES = ['Todo', 'Requirements → Client', 'Ready', 'In Dev', 'QA / Internal Review', 'Blocked'];
    const GO_LIVE_ISSUE_STATUSES = ['Ready for Go-Live'];

    if (metric === 'issuesIntoUat') {
      return `Issues Into UAT (This Month)${titleSuffix}`;
    }
    if (metric === 'issuesCompleted') {
      return `Completed Issues (This Month)${titleSuffix}`;
    }
    if (type === 'doneThisMonth' && issueStatus) {
      return `Issues - ${issueStatus} (This Month)${titleSuffix}`;
    }

    // Integrator/PM breakdown table uses these filters for the columns.
    if (type === 'all' && Array.isArray(issueStatuses) && isSameSet(issueStatuses, BUILDING_ISSUE_STATUSES)) {
      return `Issues - Building${titleSuffix}`;
    }
    if (type === 'all' && issueStatus === 'UAT / Client Review') {
      return `Issues - UAT${titleSuffix}`;
    }
    if (type === 'completed' && Array.isArray(issueStatuses) && isSameSet(issueStatuses, GO_LIVE_ISSUE_STATUSES)) {
      return `Issues - Go Live${titleSuffix}`;
    }
    if (type === 'all' && !issueStatus && (!issueStatuses || issueStatuses.length === 0)) {
      return `Issues - Total${titleSuffix}`;
    }

    // Issues by status table uses `issueStatus` for total/backlogged/falloff.
    if (!type && issueStatus) {
      return `Issues - ${issueStatus}${titleSuffix}`;
    }

    if (type === 'backlogged') {
      return (
        <span className="title-with-info">
          {issueStatus ? `Issues - ${issueStatus} (Backlogged)${titleSuffix}` : `Backlogged Issues${titleSuffix}`}
          <Info title={`Issues that are flagged as backlogged.\n\nAn issue is backlogged if:\n• Its due date (or project target date fallback) is overdue\n• OR it has been in Todo / Requirements → Client for > 4 weeks (and is not Blocked Client)\n• OR it has been in the dev band (Ready, In Dev, QA / Internal Review, UAT / Client Review) for > 8 weeks`} />
        </span>
      );
    }
    if (type === 'falloff') {
      return (
        <span className="title-with-info">
          {issueStatus ? `Issues - ${issueStatus} (Falloff)${titleSuffix}` : `Issues Falling Off Next Month${titleSuffix}`}
          <Info title="Issues that are flagged as falloff (due date / target date is less than 1 month away)." />
        </span>
      );
    }
    if (type === 'completed') {
      if (endDate) {
        return `Completed Issues (Month to ${endDate})${titleSuffix}`;
      }
      return `Completed Issues (This Month)${titleSuffix}`;
    }
    if (status) {
      return `Issues - ${status}${titleSuffix}`;
    }
    return `All Issues${titleSuffix}`;
  };

  const getBreadcrumb = () => {
    if (mode === 'projects' && currentView === 'issues' && selectedProject) {
      return [
        {
          label: getProjectsModalTitle(),
          onClick: handleBackToProjects
        },
        {
          label: selectedProject.name
        }
      ];
    }
    return null;
  };

  const renderSearchBar = () => {
    if (!shouldShowSearch()) {
      return null;
    }

    const isProjectsView = mode === 'projects' && currentView === 'projects';
    const value = isProjectsView ? projectSearchQuery : issuesSearchQuery;
    const placeholder = isProjectsView ? 'Search projects...' : 'Search issues...';
    const handleChange = isProjectsView ? handleProjectSearchChange : handleIssuesSearchChange;

    return (
      <div className="modal-search-bar">
        <Input icon={FiSearch} value={value} onChange={handleChange} placeholder={placeholder} />
      </div>
    );
  };

  // Filtered projects for projects view
  const filteredProjects = useMemo(() => {
    if (!projects || !projects.length) {
      return [];
    }

    if (!projectSearchQuery || !projectSearchQuery.trim()) {
      return projects.map((project) => ({
        ...project,
        projectTargetDate: project.targetDate
      }));
    }

    const query = projectSearchQuery.toLowerCase().trim();

    return projects
      .filter((project) => {
        const nameMatch = project.name?.toLowerCase().includes(query);
        const statusMatch = project.status?.toLowerCase().includes(query);
        const assignedToMatch = project.integrationsEngineerName?.toLowerCase().includes(query);
        return nameMatch || statusMatch || assignedToMatch;
      })
      .map((project) => ({
        ...project,
        projectTargetDate: project.targetDate
      }));
  }, [projects, projectSearchQuery]);

  // Filtered issues for projects → issues view
  const filteredProjectIssues = useMemo(() => {
    if (!selectedProject) {
      return [];
    }

    if (!projectIssues || !projectIssues.length) {
      return [];
    }

    if (!issuesSearchQuery || !issuesSearchQuery.trim()) {
      return projectIssues;
    }

    const query = issuesSearchQuery.toLowerCase().trim();

    return projectIssues.filter((issue) => {
      const identifierMatch = issue.identifier?.toLowerCase().includes(query);
      const titleMatch = issue.title?.toLowerCase().includes(query);
      const stateMatch = issue.state?.toLowerCase().includes(query);
      const priorityMatch = issue.priorityLabel?.toLowerCase().includes(query);
      return identifierMatch || titleMatch || stateMatch || priorityMatch;
    });
  }, [projectIssues, issuesSearchQuery, selectedProject]);

  // Filtered issues for cross-project issues view
  const filteredIssues = useMemo(() => {
    if (mode !== 'issues') {
      return [];
    }

    if (!allIssues || allIssues.length === 0) {
      return [];
    }

    let filtered = [...allIssues];
    const isTimelineFiltered = !!(metric && startDate && endDate);

    const projectsMap = new Map();
    issueProjects.forEach((project) => {
      projectsMap.set(String(project.id), project);
    });

    // NOTE: Server already applies `type`, `issueStatus`, `issueStatuses`, `integrator`, `pmId`, and `metric` filters.
    // Keep client-side filtering limited to UI-only concerns (project-status scoping + search).

    // `status` here refers to *project status*. When we open the modal from a graph dot,
    // the API already applied the correct timeline-based filter; applying project-status
    // filtering can hide valid results (e.g. an issue entered UAT earlier in the month,
    // but the project is now in Go Live).
    if (status && !isTimelineFiltered) {
      filtered = filtered.filter((issue) => {
        const projectId = issue.projectId || issue.project_id;
        if (projectId == null || projectId === '') {
          return false;
        }
        const project = projectsMap.get(String(projectId));
        return project?.status === status;
      });
    }

    if (issuesSearchQuery && issuesSearchQuery.trim()) {
      const query = issuesSearchQuery.toLowerCase().trim();
      filtered = filtered.filter((issue) => {
        const titleMatch = issue.title?.toLowerCase().includes(query);
        const identifierMatch = issue.identifier?.toLowerCase().includes(query);
        const projectMatch = issue.projectName?.toLowerCase().includes(query);
        const stateMatch = issue.state?.toLowerCase().includes(query);
        return titleMatch || identifierMatch || projectMatch || stateMatch;
      });
    }

    return filtered;
  }, [mode, allIssues, issueProjects, status, issuesSearchQuery, startDate, endDate, metric]);

  const getProjectInitialSort = () => {
    if (type === 'backlogged') {
      return { sortBy: 'dueDate', sortDirection: 'desc' };
    }
    if (type === 'falloff') {
      return { sortBy: 'dueDate', sortDirection: 'asc' };
    }
    if (type === 'oldestBacklog') {
      return { sortBy: 'backlogDays', sortDirection: 'asc' };
    }
    return { sortBy: 'dueDate', sortDirection: 'asc' };
  };

  const projectInitialSort = getProjectInitialSort();

  // Projects columns (union of projects + finance grids)
  const projectColumns = useMemo(
    () => [
      {
        key: 'companyName',
        displayName: 'Organization',
        type: 'select',
        width: '150px',
        options: companyOptions,
        useSearch: true,
        nullable: false,
        getValue: (row) => row.companyId,
        getOptionLabel: (option, row) => option?.label || row.companyName || '-',
        onChange: (newCompanyId, { row, option }) => {
          projectActions.set('companyId', newCompanyId, { projectId: row.id });
          projectActions.set('companyName', option?.label || null, { projectId: row.id });

          if (newCompanyId !== row.companyId) {
            projectActions.set('apps', [], { projectId: row.id });
          }
        },
        onSearch: handleCompanySearch,
        sortable: true,
        sortKey: 'companyName'
      },
      {
        key: 'name',
        displayName: 'Project Name',
        type: 'text',
        width: 'minmax(220px, 1.5fr)',
        getValueStyle: ({ row }) => ({ color: row.isBacklogged ? '#dc2626' : undefined }),
        sortable: true
      },
      {
        key: 'status',
        displayName: 'Status',
        type: 'select',
        width: '130px',
        options: STATUS_OPTIONS,
        variant: 'tag',
        getValue: (row) => row.status,
        getValueStyle: ({ option, row }) => getStatusStyle(option?.value || row.status),
        onChange: (newStatus, { row }) => {
          projectActions.set('status', newStatus, { projectId: row.id });
        },
        sortable: true
      },
      {
        key: 'statusAsOfDate',
        displayName: 'Status As Of',
        type: 'text',
        width: '90px',
        formatValue: (value) => {
          if (!value) {
            return '-';
          }
          const date = new Date(value);
          if (Number.isNaN(date.getTime())) {
            return '-';
          }
          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        },
        sortable: true
      },
      {
        key: 'projectHealth',
        displayName: 'Project Health',
        type: 'button',
        width: '140px',
        sortable: true,
        renderButton: ({ row }) => {
          const health = row.projectHealth;
          const option = PROJECT_HEALTH_OPTIONS.find((o) => o.value === health);
          const color = PROJECT_HEALTH_COLORS[health];

          return (
            <div
              role="button"
              onClick={(e) => {
                e.stopPropagation();
                setHealthModalProject(row);
              }}
              className="health-cell">
              <span className="health-dot" style={{ backgroundColor: color || '#dce3e8' }} />
              <span style={{ color: color || '#899ba5' }}>{option?.label || 'Not Set'}</span>
            </div>
          );
        }
      },
      {
        key: 'targetDate',
        displayName: 'SLA Date',
        type: 'date',
        width: '140px',
        emptyDateLabel: 'No due date',
        onChange: (newDate, { row }) => {
          projectActions.set('targetDate', newDate, { projectId: row.id });
        },
        sortable: true
      },
      {
        key: 'projectTargetDate',
        displayName: 'Target Date',
        type: 'date',
        width: '140px',
        getValue: (row) => row.targetDate,
        emptyDateLabel: 'No date',
        onChange: (newDate, { row }) => {
          projectActions.set('targetDate', newDate, { projectId: row.id });
        },
        sortable: true,
        sortKey: 'projectTargetDate'
      },
      {
        key: 'backlogDays',
        displayName: 'Days Backlogged',
        type: 'text',
        width: '130px',
        formatValue: (value, row) => {
          if (row?.backlogDays == null) {
            return 0;
          }
          return row.backlogDays;
        },
        getValueStyle: ({ row }) => {
          const value = row?.backlogDays ?? 0;
          const isNegative = value < 0;
          return {
            fontSize: 14,
            fontWeight: 500,
            color: isNegative ? '#dc2626' : '#2563eb'
          };
        },
        sortable: true,
        hidden: type !== 'oldestBacklog'
      },
      {
        key: 'integrationsEngineerId',
        displayName: 'Assigned To',
        type: 'select',
        width: '140px',
        options: integratorsOptions,
        useSearch: true,
        nullable: true,
        getValue: (row) => row.integrationsEngineerId,
        getOptionLabel: (option, row) => option?.label || row.integrationsEngineerName || '-',
        onChange: (newLeadId, { row, option }) => {
          const integratorName = option?.label || null;
          projectActions.set('leadId', newLeadId, { projectId: row.id });
          projectActions.set('integrationsEngineerId', newLeadId, { projectId: row.id });
          projectActions.set('integrationsEngineerName', integratorName, { projectId: row.id });
        },
        sortable: true,
        sortKey: 'integrationsEngineerName'
      },
      {
        key: 'accountRepId',
        displayName: 'Account Representative',
        type: 'select',
        width: '140px',
        options: accountRepOptions,
        useSearch: true,
        nullable: true,
        getValue: (row) => row.accountRepId,
        getOptionLabel: (option) => option?.label || '-',
        onChange: (newRepId, { row }) => {
          projectActions.set('accountRepId', newRepId, { projectId: row.id });
        },
        sortable: true,
        sortKey: 'accountRepName'
      },
      {
        key: 'clientUpdatedOn',
        displayName: 'Client Updated On',
        type: 'date',
        width: '140px',
        emptyDateLabel: 'No date',
        onChange: (newDate, { row }) => {
          projectActions.set('clientUpdatedOn', newDate, { projectId: row.id });
        },
        sortable: true
      },
      {
        key: 'invoiceSent',
        displayName: 'Invoice Sent',
        type: 'checkbox',
        width: '80px',
        checkboxLabel: 'Mark invoice as sent',
        onChange: (newValue, { row }) => {
          projectActions.set('invoiceSent', newValue, { projectId: row.id });
        },
        sortable: true
      },
      {
        key: 'actions',
        displayName: '',
        type: 'button',
        width: '60px',
        sortable: false,
        renderButton: ({ row }) => (
          <div className="action-cell">
            {row.linearProjectId && (
              <a href={`https://linear.app/mindcloud/project/${row.linearProjectId}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} data-title="Open in Linear">
                <div role="button" className="action-button" data-title="Open in Linear">
                  <IconV2 icon={SiLinear} />
                </div>
              </a>
            )}
            <div
              role="button"
              className="action-button"
              onClick={(e) => {
                e.stopPropagation();
                handleProjectClick(row);
              }}
              data-title="View project issues">
              <IconV2 icon={FiChevronRight} />
            </div>
          </div>
        )
      }
    ],
    [accountRepOptions, companyOptions, handleCompanySearch, integratorsOptions, type]
  );

  const getProjectRowClassName = (row) => {
    const classes = ['project-list-row'];
    if (row?.isBacklogged) {
      classes.push('backlogged');
    }
    return classes.join(' ');
  };

  // Shared issues columns for both modes (with optional project column)
  const issuesColumns = useMemo(
    () => [
      {
        key: 'projectName',
        displayName: 'Project',
        type: 'select',
        width: 'minmax(200px, 1.2fr)',
        options: issueProjectsOptions,
        useSearch: true,
        nullable: true,
        getValue: (row) => row.projectId || row.project_id,
        getOptionLabel: (option, row) => option?.label || row.projectName || '-',
        onChange: (newProjectId, { row, option }) => {
          const issueId = row.id;
          projectIssueActions.set('projectId', newProjectId, { issueId });
          projectIssueActions.set('projectName', option?.label || null, { issueId });
        },
        sortable: true,
        sortKey: 'projectName',
        hidden: mode === 'projects' && currentView === 'issues'
      },
      {
        key: 'identifier',
        displayName: 'Issue',
        type: 'text',
        width: 'minmax(220px, 1.6fr)',
        formatValue: (value, row) => `${value} / ${row.title || ''}`,
        sortable: true,
        sortKey: 'identifier'
      },
      {
        key: 'workflowName',
        displayName: 'Workflow',
        type: 'select',
        width: 'minmax(180px, 1fr)',
        options: workflowOptions,
        useSearch: true,
        nullable: true,
        getValue: (row) => row.workflowId || row.workflow_id,
        getOptionLabel: (option, row) => option?.label || row.workflowName || '-',
        onChange: (newWorkflowId, { row, option }) => {
          const issueId = row.id;
          const newWorkflowName = option?.label && option.label !== 'None' ? option.label : null;
          projectIssueActions.set('workflowId', newWorkflowId, { issueId });
          projectIssueActions.set('workflowName', newWorkflowName, { issueId });
        },
        onOpen: ({ row }) => {
          if (!row.companyId) {
            return;
          }
          setCurrentWorkflowCompanyId(row.companyId);
          getCompanyWorkflows({ companyId: row.companyId });
        },
        sortable: true,
        sortKey: 'workflowName'
      },
      {
        key: 'state',
        displayName: 'Status',
        type: 'select',
        width: '140px',
        options: ISSUE_STATUS_OPTIONS,
        variant: 'tag',
        getValue: (row) => row.state,
        getValueStyle: ({ option, row }) => getIssueStatusStyle(option?.value || row.state),
        onChange: (newState, { row }) => {
          const issueId = row.id;
          const projectId = row.projectId || row.project_id;
          projectIssueActions.set('state', newState, { issueId, projectId });
        },
        sortable: true,
        sortKey: 'state',
        sortComparator: (a, b) => {
          return (ISSUE_STATE_SORT_ORDER[a] ?? 999) - (ISSUE_STATE_SORT_ORDER[b] ?? 999);
        }
      },
      {
        key: 'priority',
        displayName: 'Priority',
        type: 'select',
        width: '130px',
        options: PRIORITY_OPTIONS,
        variant: 'tag',
        getValue: (row) => row.priority,
        getValueStyle: ({ row }) => getPriorityStyle(row.priority),
        onChange: (newPriority, { row }) => {
          const issueId = row.id;
          const projectId = row.projectId || row.project_id;
          projectIssueActions.set('priority', newPriority, { issueId, projectId });
        },
        sortable: true,
        sortComparator: (a, b) => {
          return (PRIORITY_SORT_ORDER[a] ?? 999) - (PRIORITY_SORT_ORDER[b] ?? 999);
        }
      },
      {
        key: 'assigneeName',
        displayName: 'Assigned To',
        type: 'select',
        width: '150px',
        options: integratorsOptions,
        useSearch: true,
        nullable: true,
        getValue: (row) => row.assigneeId || row.assignee,
        getOptionLabel: (option, row) => option?.label || row.assigneeName || '-',
        onChange: (newAssigneeId, { row, option }) => {
          const issueId = row.id;
          const projectId = row.projectId || row.project_id;
          projectIssueActions.set('assigneeId', newAssigneeId, { issueId, projectId });
          projectIssueActions.set('assigneeName', option?.label || null, { issueId, projectId });
        },
        sortable: true
      },
      {
        key: 'blockers',
        displayName: 'Blockers',
        type: 'select',
        width: '130px',
        options: [
          { label: 'Waiting on Dev', value: 'waiting_on_dev' },
          { label: 'Waiting on Client', value: 'waiting_on_client' }
        ],
        useSearch: false,
        nullable: true,
        getValue: (row) => row.blockers || null,
        getOptionLabel: (option) => option?.label || '-',
        onChange: (newBlockersValue, { row }) => {
          const issueId = row.id;
          const projectId = row.projectId || row.project_id;
          projectIssueActions.set('blockers', newBlockersValue, { issueId, projectId });
        },
        sortable: true
      },
      {
        key: 'dueDate',
        displayName: 'Due Date',
        type: 'date',
        width: '140px',
        emptyDateLabel: 'No due date',
        onChange: (newDueDate, { row }) => {
          const issueId = row.id;
          const projectId = row.projectId || row.project_id;
          projectIssueActions.set('dueDate', newDueDate, { issueId, projectId });
        },
        sortable: true
      },
      {
        key: 'actions',
        displayName: '',
        type: 'button',
        width: '60px',
        sortable: false,
        renderButton: ({ row }) => (
          <div className="action-cell">
            <div
              role="button"
              className="action-button"
              onClick={(e) => {
                e.stopPropagation();
                handleIssueClick(row.url);
              }}
              data-title="Open in Linear">
              <IconV2 icon={SiLinear} />
            </div>
          </div>
        )
      }
    ],
    [currentView, integratorsOptions, issueProjectsOptions, mode, workflowOptions]
  );

  const modalClassName = mode === 'projects' && currentView === 'issues' ? 'project-issues-modal' : mode === 'projects' ? 'projects-list-modal' : 'issues-modal';

  const modalTitle = mode === 'projects' ? (currentView === 'projects' ? getProjectsModalTitle() : null) : getIssuesModalTitle();

  return (
    <ModalV2 show={show} onHide={handleClose} title={modalTitle} breadcrumb={getBreadcrumb()} maxWidth="1200px" className={modalClassName} centered header={renderSearchBar()}>
      <Page.Section variant="modal" ref={modalRef}>
        <ScrollContainerV2 style={{ height: '700px' }}>
          {mode === 'projects' && currentView === 'projects' && (
            <>
              <GridTable loading={loadingProjects} rows={filteredProjects} columns={projectColumns} className="grid-table" rowKey="id" getRowClassName={getProjectRowClassName} emptyStateTitle="No projects found" initialSort={projectInitialSort} />
            </>
          )}

          {mode === 'projects' && currentView === 'issues' && <GridTable loading={loadingProjectIssues} rows={filteredProjectIssues} columns={issuesColumns} className="grid-table" rowKey="id" emptyStateTitle="No issues found" initialSort={{ sortBy: 'identifier', sortDirection: 'asc' }} enablePagination={false} />}

          {mode === 'issues' && (
            <>
              <div className="issues-table-container">
                <GridTable loading={loadingAllIssues} rows={filteredIssues} columns={issuesColumns} className="grid-table" rowKey="id" emptyStateTitle="No issues found" initialSort={{ sortBy: 'projectName', sortDirection: 'asc' }} enablePagination={true} initialPage={1} itemsPerPage={50} />
              </div>
            </>
          )}
        </ScrollContainerV2>
      </Page.Section>
      <ProjectHealthModal show={!!healthModalProject} onHide={() => setHealthModalProject(null)} project={healthModalProject} integrators={integratorsOptions} />
    </ModalV2>
  );
};

export default IntegrationsOverviewModal;
